import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PayloadLocale } from '@fluenceur/types';
import { useOutreachLanguage } from './use-outreach-language';

/**
 * The localized default email subject shared by both outreach callers (the
 * affiliation email step and the sponsored proposal module). The template
 * carries no subject, so we ship this default — resolved in the BODY language
 * (`selectedLanguage`), not the UI locale, so a French body never ends up under
 * an English subject. Factored here so the two callers can't drift apart.
 */
export type OutreachSubjectKind = 'collaboration' | 'affiliation';

/**
 * Picks the default subject for the body language.
 *
 * `en` and `fr` always come from the dashboard bundles — they are the brand
 * surfaces' own languages and stay the source of truth. Italian has no
 * dashboard bundle, so it is served by the backend (`defaultSubjects.it`) when
 * available and falls back to the bundled default otherwise.
 */
export const resolveOutreachDefaultSubject = (
  language: PayloadLocale,
  bundledSubject: string,
  italianSubject?: string
): string => (language === 'it' && italianSubject ? italianSubject : bundledSubject);

export const useOutreachEmailSubject = (
  brandName: string,
  kind: OutreachSubjectKind = 'collaboration',
  italianSubject?: string
): string => {
  const { i18n } = useTranslation();
  const { selectedLanguage } = useOutreachLanguage();
  return useMemo(
    () =>
      resolveOutreachDefaultSubject(
        selectedLanguage,
        i18n.getFixedT(selectedLanguage)(
          kind === 'affiliation'
            ? 'invitations.modal.affiliation.emailSubject'
            : 'invitations.modal.collab.emailSubject',
          { brandName, first_name: '{{first_name}}' }
        ),
        italianSubject
      ),
    [i18n, selectedLanguage, brandName, kind, italianSubject]
  );
};
